import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { TealiumAnalyticsService } from '@ddt-web/analytics';
import { ERROR_CODES, ERROR_SEVERITY, ErrorDCR } from '@ddt-web/config';

import { ErrorObject } from '../models/error-object';
import { PopupDialogAction, PopupDialogObject } from '../models/popup-dialog-object';
import { WweErrorPortfolioDetails } from '../models/wwe-errors/wwe-security-details-partial-error';
import { WweErrorUtil } from '../utils/wwe-error.util';
import { AbstractErrorHandlerService } from './abstract-error-handler.service';
import { ErrorHelperService } from './error-helper.service';
import { ErrorPopupService } from './error-popup.service';

@Injectable()
export class PiSecurityErrorHandlerService extends AbstractErrorHandlerService {
	private flagErrorConfigs = ['performance', 'risk', 'details', 'associatedPortfolios'];

	constructor(
		protected contentService: ContentService,
		protected errorPopupService: ErrorPopupService,
		protected tealiumAnalyticsService: TealiumAnalyticsService,
		protected errorHelperService: ErrorHelperService
	) {
		super(
			contentService,
			errorPopupService,
			tealiumAnalyticsService,
			errorHelperService
		);
	}

	public handleError(error): void {
		const configName = error.configName || '';
		if (error instanceof ErrorObject && this.flagErrorConfigs.includes(configName)) {
			let errorCodes = [];
			if (error.responseJson?.errorCodes && !Array.isArray(error.responseJson.errorCodes)) {
				errorCodes = Object.values(error.responseJson.errorCodes).map(error => error['errorCode']);
			} else {
				errorCodes = error.responseJson?.errorCodes || [];
			}
			const missingACServerSecurity = errorCodes.includes(ERROR_CODES.SECURITY_NOT_FOUND);

			if (missingACServerSecurity) {
				const errorHoldings = this.handleACServerMissingSecurities(error);
				if (errorHoldings.length) {
					const firstFailedPortfolioId = error.responseJson.resultList?.[0].uniqueId;
					const title = this.contentService.get('MISSING_POSITION_MAPPING_TITLE', ErrorDCR.MISSING_POSITION_MAPPING_TITLE);
					const errorMessage = this.contentService.get('MISSING_POSITION_MAPPING_MESSAGE', ErrorDCR.MISSING_POSITION_MAPPING_MESSAGE);
					const actions = this.buildViewPortfolioInEditorAction(firstFailedPortfolioId);
					const trackErrorMessage = 'unabletoprocessrequest';

					this.errorSeverity = ERROR_SEVERITY.ERROR;
					const listings = errorHoldings.map(holding => {
						return { iconType: 'times', description: holding };
					});
					console.error('There was an error: Missing holding or position', error);
					this.tealiumAnalyticsService.trackError(trackErrorMessage, this.errorSeverity);

					this.errorPopupService.openResponseMessageDialog(
						this.buildErrorMessage(false, 'error-exclude-ticker-dialog', title, trackErrorMessage, errorMessage, false, listings, [actions]),
						true
					);
				}
			} else {
				const errorObject = error.responseJson;
				const wweErrorPortfolioDetails: WweErrorPortfolioDetails = { id: errorObject.portfolioUniqueId, description: null };
				const dialogErrorMessage = this.handleWWEDataErrorMessage([wweErrorPortfolioDetails]);
				this.errorPopupService.openResponseMessageDialog(dialogErrorMessage, false);
			}
		} else {
			this.nextHandler.handleError(error);
		}
	}

	private handleWWEDataErrorMessage(errorPortfolios: WweErrorPortfolioDetails[]): PopupDialogObject {
		const description = this.contentService.get('PI_WWE_DATA_ERROR', 'Unable to retrieve analysis data for this portfolio. Please view the portfolio in the 360 Evaluator.');
		const dialogErrorMessage = this.errorHelperService.buildPopupDialogObject(description, null, false, this.buildErrorMessage());
		dialogErrorMessage.actions = [this.buildViewPortfolioIn360Action(errorPortfolios?.[0]?.id || '')];
		dialogErrorMessage.disableClose = false;
		return dialogErrorMessage;
	}

	private buildViewPortfolioIn360Action(firstFailedPortfolioId: string): PopupDialogAction {
		return PopupDialogAction.create({
			label: this.contentService.get('PI_VIEW_IN_360', 'View in 360 Evaluator'),
			type: 'view_360',
			reload: false,
			portfolioId: firstFailedPortfolioId
		});
	}

	private buildViewPortfolioInEditorAction(firstFailedPortfolioId: string): PopupDialogAction {
		return WweErrorUtil.buildViewPortfolioInEditorAction(
			firstFailedPortfolioId,
			this.contentService.get('ERROR_REVIEW_PORTFOLIO_HOLDINGS_LABEL', ErrorDCR.ERROR_REVIEW_PORTFOLIO_HOLDINGS_LABEL));
	}

	private handleACServerMissingSecurities(error): string[] {
		const errorMap = error.responseJson.errorCodes || [];
		const missingSecurities = Object.keys(errorMap)
			.filter(obj => errorMap[obj].errorCode === ERROR_CODES.SECURITY_NOT_FOUND)
			.reduce((obj, key) => {
				obj[key] = errorMap[key];
				return obj;
			}, {});
		return Object.keys(missingSecurities).filter(holdingId => holdingId.includes('@')).map(holdingId => holdingId.split(/[:@]+/)[0]);
	}
}
